// The signed-in member's marks on the text. One query per chapter, so the
// chapter page asks once and every block on it reads from the same answer.
import type {
  BookmarkInput,
  BookmarkView,
  HighlightInput,
  HighlightView,
  LibraryChapterView,
  LibraryView,
  MemberNoteInput,
  MemberNoteView,
} from "@bibleunleashed/shared";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api, ApiError, del, post, put } from "./api";
import { useUser } from "./auth";
import { track } from "./events";

const chapterKey = (osis: string) => ["library", "chapter", osis];
const ALL = ["library", "all"];

const EMPTY: Omit<LibraryChapterView, "osis"> = { highlights: [], notes: [], bookmarks: [] };

/**
 * A chapter's highlights, notes and bookmarks. A visitor has none rather than
 * an error: the reading page is the same page whether or not anyone is signed
 * in, and nothing on it should be conditional on the query having run.
 */
export function useChapterLibrary(osis: string | undefined) {
  const { data: user } = useUser();
  const query = useQuery({
    queryKey: chapterKey(osis ?? ""),
    queryFn: () => api<LibraryChapterView>(`/api/library/chapter/${encodeURIComponent(osis!)}`),
    enabled: Boolean(osis && user),
    staleTime: 60_000,
  });
  return { ...EMPTY, ...query.data, isLoading: query.isLoading };
}

export function useLibrary() {
  return useQuery({
    queryKey: ALL,
    queryFn: async () => {
      try {
        return await api<LibraryView>("/api/library");
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) return { chapters: [] };
        throw err;
      }
    },
  });
}

/** Both views of the same rows; a change to one has to invalidate the other. */
const refresh = (qc: QueryClient, osis: string) => {
  void qc.invalidateQueries({ queryKey: chapterKey(osis) });
  void qc.invalidateQueries({ queryKey: ALL });
};

export function useSaveHighlight(osis: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: HighlightInput) => post<HighlightView>("/api/library/highlights", body),
    onSuccess: (_view, body) => {
      track({ type: "highlight", ref: body.ref });
      refresh(qc, osis);
    },
  });
}

/** Clearing a verse rather than one highlight: what the context menu offers. */
export function useClearHighlights(osis: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ref: string) => del<null>(`/api/library/highlights/verse/${encodeURIComponent(ref)}`),
    onSuccess: () => refresh(qc, osis),
  });
}

export function useDeleteHighlight(osis: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<null>(`/api/library/highlights/${id}`),
    onSuccess: () => refresh(qc, osis),
  });
}

/** Saving an empty note is how a note is deleted; the server treats it so. */
export function useSaveNote(osis: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: MemberNoteInput) => put<MemberNoteView | null>("/api/library/notes", body),
    onSuccess: (_view, body) => {
      if (body.bodyMd.trim()) track({ type: "note", ref: body.ref }); // an empty save deletes the note
      refresh(qc, osis);
    },
  });
}

export function useSaveBookmark(osis: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: BookmarkInput) => put<BookmarkView>("/api/library/bookmarks", body),
    onSuccess: (_view, body) => {
      track({ type: "bookmark", ref: body.ref });
      refresh(qc, osis);
    },
  });
}

export function useDeleteBookmark(osis: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del<null>(`/api/library/bookmarks/${id}`),
    onSuccess: () => refresh(qc, osis),
  });
}
